import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Svadlenka = {
  name: string;
  orders: number;
  avg_h: number;
  median_h: number;
  total_h: number;
  avg_ks: number;
  buckets: number[];
  cat_ratio: number;
};

type Mesic = { month: string; name: string; orders: number; avg_h: number; total_h: number };

type Data = { svadlenky: Svadlenka[]; mesice: Mesic[] };

const MESICE = [
  'leden',
  'únor',
  'březen',
  'duben',
  'květen',
  'červen',
  'červenec',
  'srpen',
  'září',
  'říjen',
  'listopad',
  'prosinec',
];

/** Skupiny délky zakázky — musí sedět s BUCKETS v api/lib/delkaZakazek.ts. */
const SKUPINY = [
  { label: 'do 1 h', cls: 'bg-danger' },
  { label: '1–2 h', cls: 'bg-warning' },
  { label: '2–4 h', cls: 'bg-primary' },
  { label: 'nad 4 h', cls: 'bg-success' },
];

function ym(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Měsíc o `n` zpět od aktuálního. */
function monthsAgo(n: number): string {
  const d = new Date();
  return ym(new Date(d.getFullYear(), d.getMonth() - n, 1));
}

function fmtMonth(m: string): string {
  const [y, mm] = m.split('-');
  return `${MESICE[Number(mm) - 1] ?? mm} ${y}`;
}

/** Hodiny s jedním desetinným místem a čárkou. */
function fmtH(h: number): string {
  return `${(Math.round(h * 10) / 10).toFixed(1).replace('.', ',')} h`;
}

const PRESETY = [
  { label: 'Tento měsíc', od: () => monthsAgo(0) },
  { label: '3 měsíce', od: () => monthsAgo(2) },
  { label: '6 měsíců', od: () => monthsAgo(5) },
  { label: 'Letos', od: () => `${new Date().getFullYear()}-01` },
];

/**
 * Délka zakázek švadlen — jak dlouhé zakázky (podle normy) která švadlenka
 * dostává. Pomáhá ohlídat, že střihárna rozděluje práci férově.
 */
export function DelkaZakazekPage() {
  const [od, setOd] = useState(() => monthsAgo(2));
  const [doMesic, setDoMesic] = useState(() => monthsAgo(0));
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(
        await apiFetch<Data>(
          `/api/vyroba/striharna/delka-zakazek?od=${encodeURIComponent(od)}&do=${encodeURIComponent(doMesic)}`,
        ),
      );
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Nepodařilo se načíst zakázky.');
    } finally {
      setLoading(false);
    }
  }, [od, doMesic]);

  useEffect(() => {
    void load();
  }, [load]);

  const svadlenky = data?.svadlenky ?? [];
  const maxAvg = Math.max(0, ...svadlenky.map((s) => s.avg_h));
  // Průměr všech zakázek v období — měřítko „kdo je pod/nad".
  const celkem = useMemo(() => {
    const orders = svadlenky.reduce((s, x) => s + x.orders, 0);
    const hours = svadlenky.reduce((s, x) => s + x.total_h, 0);
    return orders > 0 ? hours / orders : 0;
  }, [svadlenky]);

  const months = useMemo(
    () => [...new Set((data?.mesice ?? []).map((m) => m.month))].sort(),
    [data],
  );
  const cell = useMemo(() => {
    const map = new Map<string, Mesic>();
    for (const m of data?.mesice ?? []) map.set(`${m.month}|${m.name}`, m);
    return map;
  }, [data]);
  // V každém měsíci zvýrazníme švadlenku s nejkratšími zakázkami.
  const nejkratsi = useMemo(() => {
    const out = new Map<string, string>();
    for (const month of months) {
      const list = (data?.mesice ?? []).filter((m) => m.month === month && m.orders >= 5);
      const min = list.reduce<Mesic | null>((a, b) => (!a || b.avg_h < a.avg_h ? b : a), null);
      if (min && list.length > 1) out.set(month, min.name);
    }
    return out;
  }, [data, months]);

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Délka zakázek švadlen</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Jak dlouhé zakázky podle normy (předpokládaný čas šití celé zakázky) která švadlenka
            dostává. Zakázka se počítá do měsíce, kdy byla došitá.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {PRESETY.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                setOd(p.od());
                setDoMesic(monthsAgo(0));
              }}
              className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text hover:bg-surface-hover cursor-pointer"
            >
              {p.label}
            </button>
          ))}
          <label className="text-sm text-text-secondary" htmlFor="delka-od">
            Od
          </label>
          <input
            id="delka-od"
            type="month"
            value={od}
            max={doMesic}
            onChange={(e) => e.target.value && setOd(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
          />
          <label className="text-sm text-text-secondary" htmlFor="delka-do">
            Do
          </label>
          <input
            id="delka-do"
            type="month"
            value={doMesic}
            min={od}
            onChange={(e) => e.target.value && setDoMesic(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
          />
        </div>
      </div>

      {loading && <p className="text-sm text-text-muted">Načítám…</p>}
      {error && !loading && (
        <div className="rounded-lg border border-danger/20 bg-danger-light p-4 mb-6 text-sm text-danger">
          {error}{' '}
          <button type="button" onClick={() => void load()} className="underline cursor-pointer">
            Zkusit znovu
          </button>
        </div>
      )}

      {data && !loading && svadlenky.length === 0 && (
        <p className="text-sm text-text-muted">V tomto období nejsou žádné došité zakázky.</p>
      )}

      {svadlenky.length > 0 && !loading && (
        <>
          <div className="bg-surface rounded-xl shadow-card overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider font-bold text-text">
                  <th className="px-4 py-3">Švadlenka</th>
                  <th className="px-3 py-3 text-right">Zakázek</th>
                  <th className="px-3 py-3 min-w-56">Průměrná délka zakázky</th>
                  <th className="px-3 py-3 text-right">Medián</th>
                  <th className="px-3 py-3 text-right" title="1,00 = průměr kategorie">
                    Vůči kategorii
                  </th>
                  <th className="px-3 py-3 min-w-48">Rozložení zakázek</th>
                  <th className="px-3 py-3 text-right">Ø ks</th>
                  <th className="px-4 py-3 text-right">Hodin celkem</th>
                </tr>
              </thead>
              <tbody>
                {svadlenky.map((s) => (
                  <tr key={s.name} className="border-b border-border-light hover:bg-surface-hover">
                    <td className="px-4 py-3 font-semibold text-text">{s.name}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-text-secondary">
                      {s.orders}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-2.5 flex-1 rounded-full bg-bg overflow-hidden">
                          {/* Šířka pruhu je dynamická — Tailwindem nejde. */}
                          <div
                            className={`h-full rounded-full ${s.avg_h < celkem * 0.85 ? 'bg-warning' : 'bg-primary'}`}
                            style={{ width: `${maxAvg > 0 ? (s.avg_h / maxAvg) * 100 : 0}%` }}
                          />
                        </div>
                        <span className="tabular-nums text-base font-extrabold text-text w-16 text-right">
                          {fmtH(s.avg_h)}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-text-secondary">
                      {fmtH(s.median_h)}
                    </td>
                    <td
                      className={`px-3 py-3 text-right tabular-nums ${s.cat_ratio < 0.85 ? 'text-warning font-semibold' : 'text-text-secondary'}`}
                    >
                      {s.cat_ratio.toFixed(2).replace('.', ',')}
                    </td>
                    <td className="px-3 py-3">
                      <div
                        className="flex h-2.5 rounded-full overflow-hidden bg-bg"
                        title={SKUPINY.map((g, i) => `${g.label}: ${s.buckets[i] ?? 0}×`).join(
                          ', ',
                        )}
                      >
                        {SKUPINY.map((g, i) => (
                          <div
                            key={g.label}
                            className={g.cls}
                            style={{ width: `${((s.buckets[i] ?? 0) / s.orders) * 100}%` }}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-text-secondary">
                      {s.avg_ks.toFixed(1).replace('.', ',')}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-text-secondary">
                      {Math.round(s.total_h)} h
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 text-xs text-text-muted flex flex-wrap gap-x-4 gap-y-1 border-t border-border-light">
              <span>Průměr všech: {fmtH(celkem)}</span>
              {SKUPINY.map((g) => (
                <span key={g.label} className="inline-flex items-center gap-1">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${g.cls}`} />
                  {g.label}
                </span>
              ))}
              <span>
                „Vůči kategorii" porovnává se zakázkami stejného výrobku (1,00 = průměr) — očistí
                rozdíl daný jen tím, co kdo šije.
              </span>
            </div>
          </div>

          {months.length > 1 && (
            <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
              <div className="px-4 pt-4 pb-2 text-sm font-semibold text-text">
                Po měsících — průměrná délka zakázky · počet zakázek
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider font-bold text-text">
                    <th className="px-4 py-3">Měsíc</th>
                    {svadlenky.map((s) => (
                      <th key={s.name} className="px-3 py-3 text-right">
                        {s.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {months.map((month) => (
                    <tr key={month} className="border-b border-border-light">
                      <td className="px-4 py-2 text-text whitespace-nowrap">{fmtMonth(month)}</td>
                      {svadlenky.map((s) => {
                        const m = cell.get(`${month}|${s.name}`);
                        const low = nejkratsi.get(month) === s.name;
                        return (
                          <td
                            key={s.name}
                            className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${low ? 'bg-warning-light' : ''}`}
                            title={m ? `${Math.round(m.total_h)} h celkem` : undefined}
                          >
                            {m ? (
                              <>
                                <span className="font-semibold text-text">{fmtH(m.avg_h)}</span>
                                <span className="text-text-muted"> · {m.orders}×</span>
                              </>
                            ) : (
                              <span className="text-text-muted">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 text-xs text-text-muted">
                Žlutě = v daném měsíci nejkratší zakázky (počítá se od 5 zakázek).
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
